import React, { useContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  HrBlack,
  HrButtonActive,
  HrButtonHover,
  HrCharcoal,
  HrCustomButton,
  HrFlexContainer,
  HrGrey,
  HrGridContainer,
  HrIconButton,
  HrLabel,
  HrParagraph,
  HrRed,
  HrSecondary,
  HrTitleContainer,
  HrTitleH4,
  HrWhite,
  Spinner
} from '@components/styles/hr-styles';
import useTranslationWithPrefix from '@/i18n/hooks/useTranslationWithPrefix';
import {
  ALLOWED_FILE_TYPES,
  FORMAT_FILES_ACCEPTED,
  MAX_FILE_SIZE
} from '@/postulation_form/components/attached-files/constants';
import { attachedFilesReducer, calculateInitialState } from '@/postulation_form/components/attached-files/reducers';
import { Input } from '@components/form/v2/input/input';
import { useInputFileValidation } from '@/postulation_form/components/attached-files/hooks/input-file';
import useStore from '@/postulation_form/store/useStore';
import DropboxChooser from 'react-dropbox-chooser';
import { LogoDropbox } from '@/utils/image-svg';
import Toast from '@/pipeline/components/Toast';
import { useFileUploadMutation } from '@/postulation_form/components/attached-files/hooks/useFileUploadMutation';
import { useFileUploadState } from '@/postulation_form/components/attached-files/hooks/useFileUploadState';
import { useDropboxFileFetch } from '@/postulation_form/components/attached-files/hooks/useDropboxFileFetch';
import { DisplaySettingsContext } from '@/postulation_form/store/display-settings';

export const AttachedFiles = (props) => {
  const { filesRequirements, filesLoaded } = props;

  const { t } = useTranslationWithPrefix('postulant.curriculum');
  const { setFilesLoaded, deleteFileLoaded, setActionBlockedSubmit } = useStore();
  const inputRefsMap = useRef({});
  const [toastState, setToastState] = React.useState({ visible: false, message: null, typeToast: null });
  const [dropboxFile, setDropboxFile] = React.useState({ name: '', url: null, fileType: '' });
  const [filesToUpload, setFilesToUpload] = useReducer(attachedFilesReducer, filesRequirements, calculateInitialState);

  const { handleUploadSuccess, handleUploadPending, handleUploadError, handleDropboxSuccess } = useFileUploadState({
    setFilesToUpload,
    setFilesLoaded,
    setToastState,
    setDropboxFile,
    inputRefsMap
  });
  const storeTemporalAttachedFile = useFileUploadMutation({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
    onPending: handleUploadPending,
    onHandleSubmitForm: setActionBlockedSubmit,
    t
  });
  const { validationState, handleChange, reset } = useInputFileValidation({
    validateSize: MAX_FILE_SIZE,
    onFileSelect: (inputFilename, file, buttonUploader) => {
      storeTemporalAttachedFile.mutate({
        fileType: inputFilename,
        fileContent: file,
        buttonUploader
      });
    },
    fileTypes: filesToUpload.map((file) => file.type)
  });

  useDropboxFileFetch({
    dropboxFile,
    onSuccess: (dropboxData) => handleDropboxSuccess(dropboxData, handleChange),
    onError: () => {
      setToastState({ visible: true, type: 'error', message: t('errors.general') });
    }
  });

  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  const onHandleButtonClick = (fileType) => {
    inputRefsMap.current[fileType].click();
  };

  const onHandleDelete = (fileType) => {
    reset(fileType);

    if (inputRefsMap.current[fileType]) {
      inputRefsMap.current[fileType].value = '';
    }

    deleteFileLoaded(fileType);
    setFilesToUpload({ type: 'idle', filename: '', fileType });
  };

  const onHandleDropboxPicker = (data, fileType) => {
    const dropboxFileData = data[0];
    setDropboxFile({ name: dropboxFileData.name, url: dropboxFileData.link, fileType });
  };

  useEffect(() => {
    const resumeFileLoaded = filesLoaded.find((file) => file.fileType === 'resume');

    if (resumeFileLoaded && resumeFileLoaded.uploadedBy === 'cv_parser') {
      setFilesToUpload({
        type: 'uploaded',
        fileType: resumeFileLoaded.fileType,
        filename: resumeFileLoaded.filename,
        buttonUploader: 'cv-parser'
      });
    }
  }, [filesLoaded, setFilesToUpload]);

  return (
    <HrFlexContainer flexDirection='column' alignItems='flex-start' gap='24px'>
      <HrTitleContainer display='column' alignItems='flex-start'>
        <HrTitleH4 color={darkMode ? HrWhite : HrBlack} fontSizeSetting={fontSizeSetting}>
          {t('title.default')}
        </HrTitleH4>
        <HrParagraph fontWeight={600} color={darkMode ? HrWhite : HrGrey} gap='8px' fontSizeSetting={fontSizeSetting}>
          {t('subtitle')}
        </HrParagraph>
      </HrTitleContainer>

      <HrGridContainer
        columnGap='16px'
        rowGap='12px'
        padding='0 32px'
        alignItems='center'
        style={{
          gridTemplateColumns: 'repeat(3, minmax(150px, max-content))'
        }}
      >
        {filesToUpload.map((file) => (
          <React.Fragment key={file.type}>
            <HrLabel
              key={file.type}
              isRequired={file.isRequired}
              color={darkMode ? HrWhite : HrBlack}
              fontSizeSetting={fontSizeSetting}
            >
              {`${t(`title.${file.type}`)}:`}
            </HrLabel>

            {file.status === 'uploaded' && (
              <>
                <HrParagraph color={darkMode ? HrWhite : HrBlack} fontSizeSetting={fontSizeSetting}>
                  {file.filename}
                </HrParagraph>
                <HrCustomButton
                  type='button'
                  padding='8px 16px'
                  borderWidth='1px'
                  fontSize='14px'
                  width='40px'
                  height='40px'
                  color={darkMode ? HrWhite : HrBlack}
                  backgroundColor={darkMode ? HrBlack : HrWhite}
                  borderColor={darkMode ? HrWhite : HrBlack}
                  backgroundColorHover={darkMode ? HrCharcoal : HrButtonHover}
                  backgroundColorActive={darkMode ? HrCharcoal : HrButtonActive}
                  borderColorHover={darkMode ? HrWhite : HrBlack}
                  borderColorActive={darkMode ? HrWhite : HrBlack}
                  onClick={(event) => {
                    event.preventDefault();
                    onHandleDelete(file.type);
                  }}
                >
                  <i className='hr-Trash' />
                </HrCustomButton>
              </>
            )}

            {['idle', 'uploading'].includes(file.status) && (
              <>
                <HrFlexContainer display='flex' flexDirection='row' gap='16px' style={{ gridColumn: 'span 2' }}>
                  <HrFlexContainer display='column' flexDirection='column' style={{ flex: 1 }}>
                    <HrCustomButton
                      type='button'
                      padding='8px 16px'
                      borderWidth='1px'
                      fontSize='14px'
                      height='40px'
                      color={darkMode ? HrWhite : HrBlack}
                      backgroundColor={darkMode ? HrBlack : HrWhite}
                      borderColor={
                        (validationState[file.type]?.error &&
                          validationState[file.type]?.buttonUploader === 'hr-uploader' &&
                          HrRed) ||
                        (darkMode ? HrWhite : HrSecondary)
                      }
                      backgroundColorHover={darkMode ? HrCharcoal : HrButtonHover}
                      backgroundColorActive={darkMode ? HrCharcoal : HrButtonActive}
                      borderColorHover={darkMode ? HrWhite : HrBlack}
                      borderColorActive={darkMode ? HrWhite : HrBlack}
                      onClick={() => onHandleButtonClick(file.type)}
                      disabled={file.status === 'pending'}
                    >
                      {file.status === 'uploading' && file.buttonUploader === 'hr-uploader' ? (
                        <Spinner category='tertiaryGreen' className='hr-Charge' />
                      ) : (
                        <>
                          <HrIconButton className='hr-Upload' color={darkMode ? HrWhite : HrBlack} />
                          Subir archivo
                        </>
                      )}
                    </HrCustomButton>

                    {validationState[file.type]?.error &&
                      validationState[file.type]?.buttonUploader === 'hr-uploader' && (
                        <HrParagraph color={HrRed}>{validationState[file.type].error}</HrParagraph>
                      )}

                    <Input
                      type='file'
                      name={file.type}
                      accept={FORMAT_FILES_ACCEPTED.join()}
                      ref={(el) => {
                        inputRefsMap.current[file.type] = el;
                      }}
                      onChange={(event) => {
                        handleChange({
                          inputFilename: file.type,
                          buttonUploader: 'hr-uploader',
                          fileContent: event.target.files[0]
                        });
                      }}
                      style={{ display: 'none' }}
                    />
                  </HrFlexContainer>

                  <HrFlexContainer display='column' flexDirection='column' style={{ flex: 1 }}>
                    <DropboxChooser
                      appKey='8438j5ehmiwemr2'
                      success={(event) => {
                        onHandleDropboxPicker(event, file.type);
                      }}
                      linkType='direct'
                      extensions={FORMAT_FILES_ACCEPTED}
                    >
                      <HrCustomButton
                        type='button'
                        padding='8px 16px'
                        borderWidth='1px'
                        fontSize='14px'
                        height='40px'
                        color={darkMode ? HrWhite : HrBlack}
                        backgroundColor={darkMode ? HrBlack : HrWhite}
                        borderColor={
                          (validationState[file.type]?.error &&
                            validationState[file.type]?.buttonUploader === 'dropbox' &&
                            HrRed) ||
                          (darkMode ? HrWhite : HrSecondary)
                        }
                        backgroundColorHover={darkMode ? HrCharcoal : HrButtonHover}
                        backgroundColorActive={darkMode ? HrCharcoal : HrButtonActive}
                        borderColorActive={darkMode ? HrWhite : HrBlack}
                        borderColorHover={darkMode ? HrWhite : HrBlack}
                        className='dropbox-button'
                        disabled={file.status === 'pending'}
                      >
                        {file.status === 'uploading' && file.buttonUploader === 'dropbox' ? (
                          <Spinner className='hr-Charge' />
                        ) : (
                          <>
                            <LogoDropbox width={20} height={23} />
                            {t('button.dropbox')}
                          </>
                        )}
                      </HrCustomButton>

                      {validationState[file.type]?.error &&
                        validationState[file.type]?.buttonUploader === 'dropbox' && (
                          <HrParagraph color={HrRed}>{validationState[file.type].error}</HrParagraph>
                        )}
                    </DropboxChooser>
                  </HrFlexContainer>
                </HrFlexContainer>
              </>
            )}
          </React.Fragment>
        ))}
      </HrGridContainer>

      {toastState.visible && (
        <Toast text={toastState.message} isVisible={toastState.visible} type={toastState.type} msDelay={4000} />
      )}
    </HrFlexContainer>
  );
};

AttachedFiles.propTypes = {
  filesRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.number,
      type: PropTypes.oneOf(ALLOWED_FILE_TYPES).isRequired
    })
  ).isRequired,
  filesLoaded: PropTypes.arrayOf(
    PropTypes.shape({
      filename: PropTypes.string.isRequired,
      fileType: PropTypes.string.isRequired,
      uploadedBy: PropTypes.string.isRequired
    })
  ).isRequired
};
